/**
 * Refusals in words, English and Dutch: a short reason for a button that can't act now
 * (`reason.*`: "Must be placed on ore", for Place and Upgrade), and a sentence for every code the
 * rules and the server refuse with (`refused.*`, for toasts). Part of en.ts and nl.ts.
 */
import type { Message } from './types';

export const refusalsEn = {
  'reason.waiting': 'Waiting for the server',
  'reason.wrong-ground': 'Must be placed on {ground}',
  'reason.occupied': 'Something already stands here',
  'reason.protected': 'Nothing can be placed here',
  'reason.not-empty': 'Collect it first',
  'reason.pockets-full': 'Your pockets are full',
  'reason.not-loose': 'Break it with a crystal charge first',
  'reason.not-ground': 'Nothing to gather here',
  'reason.too-hard': 'Too hard for this charge',
  'reason.hopper-full': 'Its hopper is full',
  'reason.not-enough': (p) =>
    `You need ${Number(p['need']) - Number(p['have'])} more ${String(p['resource'])}`,
  'refused.bad-tile': "That tile doesn't exist.",
  'refused.time-went-back': "The server's clock hiccuped. Try again.",
  'refused.protected': 'The hub stays as it is.',
  'refused.not-enough': 'Not enough {resource}: {need} needed, you have {have}.',
  'refused.bad-machine': "There's no such machine.",
  'refused.occupied': 'Something already stands there.',
  'refused.wrong-ground': 'That needs {ground} under it.',
  'refused.no-machine': 'No machine stands there.',
  'refused.not-yours': "That's not your machine.",
  'refused.not-empty': 'Collect it first.',
  'refused.pockets-full': 'Your pockets are full: spend some first.',
  'refused.not-loose':
    'The {kind} is too solid to gather by hand: break it with a crystal charge first.',
  'refused.not-ground': "There's nothing to gather on the {kind}.",
  'refused.too-hard': 'The {kind} is too hard for this charge.',
  'refused.wrong-machine': "That machine doesn't take blocks.",
  'refused.bad-item': 'Only blocks go into an infuser.',
  'refused.hopper-full': 'Its hopper is full: let it lay some first.',
  'refused.max-level': "It's at its highest level already.",
  'refused.upgrading': "It's upgrading already.",
  'refused.empty': 'Nothing to harvest yet.',
  'refused.rate-limited': 'Slow down a little.',
  'refused.guests-cannot-create': "Guests can't make worlds: sign up to make one of your own.",
  'refused.guests-off': "Guests can't join on this server yet.",
  'refused.dev-disabled': 'Testing tools are off on this server.',
  'refused.bad-message': "Terrella sent something the server didn't understand.",
  'refused.superseded': 'That change was already handled.',
  'refused.crew-full': 'This world is full.',
  'refused.unknown-crew': 'No world with that code.',
  'refused.crew-gone': 'That world is gone.',
  'refused.not-a-member': "You're not in this world.",
  'refused.bad-name': 'Give it a name of 1 to 24 characters.',
  'refused.unauthorized': 'Sign in again.',
  'refused.network': "Can't reach Terrella. Check your connection.",
  'refused.sign-in-failed': "Signing in didn't work. Check your connection and try again.",
  'refused.unknown': "That didn't work. Try again.",
} as const satisfies Record<string, Message>;

/** Dutch: the same keys; the type check fails when one is missing. */
export const refusalsNl: Record<keyof typeof refusalsEn, Message> = {
  'reason.waiting': 'Wachten op de server',
  'reason.wrong-ground': 'Moet op {ground} staan',
  'reason.occupied': 'Hier staat al iets',
  'reason.protected': 'Hier kan niets staan',
  'reason.not-empty': 'Oogst hem eerst',
  'reason.pockets-full': 'Je zakken zijn vol',
  'reason.not-loose': 'Breek het eerst met een kristallading',
  'reason.not-ground': 'Hier valt niets te verzamelen',
  'reason.too-hard': 'Te hard voor deze lading',
  'reason.hopper-full': 'Zijn trechter is vol',
  'reason.not-enough': (p) =>
    `Je hebt nog ${Number(p['need']) - Number(p['have'])} ${String(p['resource'])} nodig`,
  'refused.bad-tile': 'Die tegel bestaat niet.',
  'refused.time-went-back': 'De klok van de server hikte. Probeer het opnieuw.',
  'refused.protected': 'De hub blijft zoals hij is.',
  'refused.not-enough': 'Niet genoeg {resource}: {need} nodig, je hebt er {have}.',
  'refused.bad-machine': 'Zo’n machine bestaat niet.',
  'refused.occupied': 'Daar staat al iets.',
  'refused.wrong-ground': 'Daar moet {ground} onder liggen.',
  'refused.no-machine': 'Daar staat geen machine.',
  'refused.not-yours': 'Dat is niet jouw machine.',
  'refused.not-empty': 'Oogst hem eerst.',
  'refused.pockets-full': 'Je zakken zijn vol: geef eerst wat uit.',
  'refused.not-loose':
    'Te vast om met de hand te verzamelen ({kind}): breek het eerst met een kristallading.',
  'refused.not-ground': 'Hier valt niets te verzamelen ({kind}).',
  'refused.too-hard': 'Te hard voor deze lading ({kind}).',
  'refused.wrong-machine': 'Die machine neemt geen blokken.',
  'refused.bad-item': 'Alleen blokken gaan in een aanlegger.',
  'refused.hopper-full': 'Zijn trechter is vol: laat hem eerst wat leggen.',
  'refused.max-level': 'Hij is al op zijn hoogste niveau.',
  'refused.upgrading': 'Hij wordt al geüpgraded.',
  'refused.empty': 'Er valt nog niets te oogsten.',
  'refused.rate-limited': 'Iets rustiger aan.',
  'refused.guests-cannot-create':
    'Gasten kunnen geen werelden maken: maak een account om er zelf een te maken.',
  'refused.guests-off': 'Gasten kunnen nog niet meedoen op deze server.',
  'refused.dev-disabled': 'Testgereedschap staat uit op deze server.',
  'refused.bad-message': 'Terrella stuurde iets wat de server niet begreep.',
  'refused.superseded': 'Die verandering was al verwerkt.',
  'refused.crew-full': 'Deze wereld is vol.',
  'refused.unknown-crew': 'Geen wereld met die code.',
  'refused.crew-gone': 'Die wereld bestaat niet meer.',
  'refused.not-a-member': 'Je zit niet in deze wereld.',
  'refused.bad-name': 'Geef een naam van 1 tot 24 tekens.',
  'refused.unauthorized': 'Meld je opnieuw aan.',
  'refused.network': 'Terrella is niet bereikbaar. Controleer je verbinding.',
  'refused.sign-in-failed':
    'Aanmelden lukte niet. Controleer je verbinding en probeer het opnieuw.',
  'refused.unknown': 'Dat lukte niet. Probeer het opnieuw.',
};
